import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Tooltip, Typography } from '@mui/material';
import { getSubStatus, getUserTypeBasedSubStatusCounts } from 'src/store/slices/generalMonitoringSlice';
import { getStatusItems } from 'src/store/slices/commonSlice';
import {
  ChartSkeleton,
  EmptyState,
  MonitorCard,
  MultiSelectFilter,
  formatNumber,
  formatPercent
} from 'src/components/monitoring/MonitoringParts';

const CUSTOMER_TYPES = ['Bayi', 'Müşteri', 'Tüzel Kişi-Şirket'];

/*
  Tek tonlu (açık → koyu turuncu) skala; eşikler önceki grafikle aynı.
  Yazı rengi her basamakta okunur kontrast verecek şekilde seçildi (koyu: >= 5.5, beyaz: >= 5.2).
*/
const SCALE = [
  { from: 0, to: 0, color: '#F5F5F5', text: '#90a4ae', name: 'Yok' },
  { from: 1, to: 50, color: '#FFE0B2', text: '#263238', name: '1–50' },
  { from: 51, to: 500, color: '#FFB74D', text: '#263238', name: '51–500' },
  { from: 501, to: 5000, color: '#FB8C00', text: '#263238', name: '501–5.000' },
  { from: 5001, to: 20000, color: '#C43E00', text: '#FFFFFF', name: '5.001–20.000' },
  { from: 20001, to: Infinity, color: '#7A2504', text: '#FFFFFF', name: '20.000+' }
];

const scaleFor = (v) => SCALE.find((s) => v >= s.from && v <= s.to) || SCALE[SCALE.length - 1];

const headCellSx = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  backgroundColor: '#fff',
  fontSize: '11px',
  fontWeight: 700,
  color: '#424242',
  textAlign: 'center',
  padding: '8px 6px',
  borderBottom: '1px solid #e0e0e0',
  whiteSpace: 'nowrap'
};

function UserTypeBasedSubStatusChart() {
  const dispatch = useDispatch();
  const [mainData, setMainData] = React.useState([]);
  const [allSubStatus, setAllSubStatus] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const statusData = useSelector((state) => state.commonSlice.statuses);
  const [selectedStatus, setStatus] = React.useState([]);
  const [hideEmpty, setHideEmpty] = React.useState(true);

  const GetUserTypeBasedSubStatusCounts = (status) => {
    setLoading(true);
    dispatch(getUserTypeBasedSubStatusCounts(status))
      .then((response) => {
        setMainData(Array.isArray(response.payload) ? response.payload : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    dispatch(getStatusItems());
    GetUserTypeBasedSubStatusCounts('0');
    dispatch(getSubStatus()).then((response) => setAllSubStatus(Array.isArray(response.payload) ? response.payload : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seçim boşsa tümü ('0')
  const handleSearchData = () => {
    const ids = (selectedStatus || []).map((row) => row.Id);
    GetUserTypeBasedSubStatusCounts(ids.length ? ids.toString() : '0');
  };

  // Satır = ara durum, sütun = müşteri tipi; toplamı büyük olan üstte
  const { rows, columnTotals, grandTotal, emptyCount } = useMemo(() => {
    const allRows = allSubStatus.map((araDurum) => {
      const values = CUSTOMER_TYPES.map((tip) => {
        const entry = mainData.find((i) => i.MUSTERI_TIPI_ACIKLAMA === tip && i.ARA_DURUM_ACIKLAMA === araDurum.ACIKLAMA);
        return entry ? Number(entry.ADET) || 0 : 0;
      });
      return { label: araDurum.ACIKLAMA, values, total: values.reduce((s, v) => s + v, 0) };
    });
    const sorted = allRows.sort((a, b) => b.total - a.total);
    const colTotals = CUSTOMER_TYPES.map((_, i) => allRows.reduce((s, r) => s + r.values[i], 0));
    return {
      rows: hideEmpty ? sorted.filter((r) => r.total > 0) : sorted,
      columnTotals: colTotals,
      grandTotal: colTotals.reduce((s, v) => s + v, 0),
      emptyCount: allRows.filter((r) => r.total === 0).length
    };
  }, [allSubStatus, mainData, hideEmpty]);

  return (
    <MonitorCard
      title="Müşteri Tipi Bazında Ara Durum Dağılımları"
      subtitle={loading ? 'Yükleniyor…' : `${formatNumber(grandTotal)} fiş · ${rows.length} ara durum`}
      action={
        <MultiSelectFilter
          label="Durum (tümü)"
          options={statusData}
          value={selectedStatus}
          onChange={setStatus}
          onApply={handleSearchData}
          disabled={loading}
        />
      }
    >
      {/* Lejant + boşları gizle */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '11px', color: '#546e7a', fontWeight: 600, mr: 0.5 }}>Fiş adedi</Typography>
          {SCALE.map((s) => (
            <Box key={s.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '4px', backgroundColor: s.color, border: s.from === 0 ? '1px solid #e0e0e0' : 'none' }} />
              <Typography sx={{ fontSize: '11px', color: '#546e7a' }}>{s.name}</Typography>
            </Box>
          ))}
        </Box>
        {emptyCount > 0 && (
          <Box
            component="button"
            type="button"
            onClick={() => setHideEmpty((v) => !v)}
            sx={{
              ml: 'auto',
              border: '1px solid #e0e0e0',
              backgroundColor: hideEmpty ? '#fff' : '#fff3e0',
              borderRadius: '8px',
              px: 1.25,
              py: 0.5,
              fontSize: '11px',
              fontWeight: 600,
              color: '#546e7a',
              cursor: 'pointer',
              fontFamily: 'inherit'
            }}
          >
            {hideEmpty ? `Boş ara durumları göster (${emptyCount})` : 'Boş ara durumları gizle'}
          </Box>
        )}
      </Box>

      {loading ? (
        <ChartSkeleton height={360} />
      ) : rows.length === 0 ? (
        <EmptyState height={240} />
      ) : (
        <Box sx={{ maxHeight: 520, overflow: 'auto', borderRadius: '12px', border: '1px solid #eceff1' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: '2px', fontFamily: 'Segoe UI' }}>
            <thead>
              <tr>
                <Box component="th" sx={{ ...headCellSx, textAlign: 'left', left: 0, zIndex: 3, minWidth: 200 }}>Ara Durum</Box>
                {CUSTOMER_TYPES.map((t) => (
                  <Box component="th" key={t} sx={{ ...headCellSx, minWidth: 110 }}>{t}</Box>
                ))}
                <Box component="th" sx={{ ...headCellSx, minWidth: 90 }}>Toplam</Box>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label}>
                  <Box
                    component="th"
                    scope="row"
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: '#fff',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#455a64',
                      padding: '6px 10px',
                      maxWidth: 320,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={r.label}
                  >
                    {r.label}
                  </Box>
                  {r.values.map((v, i) => {
                    const s = scaleFor(v);
                    return (
                      <Tooltip
                        key={CUSTOMER_TYPES[i]}
                        arrow
                        placement="top"
                        title={
                          <Box sx={{ fontSize: '12px', lineHeight: 1.6 }}>
                            <div style={{ fontWeight: 700 }}>{r.label}</div>
                            <div>{CUSTOMER_TYPES[i]}: {formatNumber(v)} fiş</div>
                            <div>Ara durum içindeki payı: {formatPercent(v, r.total)}</div>
                          </Box>
                        }
                      >
                        <Box
                          component="td"
                          sx={{
                            backgroundColor: s.color,
                            color: s.text,
                            textAlign: 'center',
                            fontSize: '12px',
                            fontWeight: v > 0 ? 700 : 400,
                            padding: '8px 6px',
                            borderRadius: '6px',
                            cursor: 'default',
                            transition: 'box-shadow 0.15s',
                            '&:hover': { boxShadow: 'inset 0 0 0 2px #263238' }
                          }}
                        >
                          {v > 0 ? formatNumber(v) : '–'}
                        </Box>
                      </Tooltip>
                    );
                  })}
                  <Box
                    component="td"
                    sx={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: '#263238', backgroundColor: '#fafafa', borderRadius: '6px', padding: '8px 6px' }}
                  >
                    {formatNumber(r.total)}
                  </Box>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <Box
                  component="th"
                  sx={{ position: 'sticky', left: 0, bottom: 0, zIndex: 2, backgroundColor: '#f5f5f5', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: '#424242', padding: '8px 10px', borderRadius: '6px' }}
                >
                  Toplam
                </Box>
                {columnTotals.map((v, i) => (
                  <Box
                    component="td"
                    key={CUSTOMER_TYPES[i]}
                    sx={{ position: 'sticky', bottom: 0, backgroundColor: '#f5f5f5', textAlign: 'center', fontSize: '12px', fontWeight: 700, color: '#263238', padding: '8px 6px', borderRadius: '6px' }}
                  >
                    {formatNumber(v)}
                    <Box component="span" sx={{ display: 'block', fontSize: '10px', fontWeight: 500, color: '#78909c' }}>
                      {formatPercent(v, grandTotal)}
                    </Box>
                  </Box>
                ))}
                <Box
                  component="td"
                  sx={{ position: 'sticky', bottom: 0, backgroundColor: '#eceff1', textAlign: 'center', fontSize: '12px', fontWeight: 800, color: '#263238', padding: '8px 6px', borderRadius: '6px' }}
                >
                  {formatNumber(grandTotal)}
                </Box>
              </tr>
            </tfoot>
          </Box>
        </Box>
      )}
    </MonitorCard>
  );
}

export default UserTypeBasedSubStatusChart;
